import SectionCard from '@/components/course/SectionCard';
import { Course } from '@/types';
import { BookOpen, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CourseContentProps {
    course: Course;
}

const CourseContent: React.FC<CourseContentProps> = ({ course }) => {
    const { t } = useTranslation();
    const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

    const allExpanded = course.sections.length > 0 && expandedSections.size === course.sections.length;

    const handleSectionToggle = (sectionId: number) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(sectionId)) {
                next.delete(sectionId);
            } else {
                next.add(sectionId);
            }
            return next;
        });
    };

    const handleToggleAll = () => {
        if (allExpanded) {
            setExpandedSections(new Set());
        } else {
            setExpandedSections(new Set(course.sections.map((s) => s.id)));
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="mb-2 text-2xl font-bold text-gray-900">
                        {t('course.courseContent')}
                    </h2>
                    <p className="text-gray-600">
                        {t('course.clickSectionToExpand')}
                    </p>
                </div>

                {course.sections.length > 0 && (
                    <button
                        onClick={handleToggleAll}
                        className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
                    >
                        {allExpanded ? (
                            <>
                                <ChevronsDownUp size={15} />
                                {t('course.collapseAll', 'Colapsar todo')}
                            </>
                        ) : (
                            <>
                                <ChevronsUpDown size={15} />
                                {t('course.expandAll', 'Expandir todo')}
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* Sections List */}
            {course.sections.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                        <BookOpen className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-900">
                        {t('course.noContentAvailable')}
                    </h3>
                    <p className="mx-auto max-w-sm text-gray-600">
                        {t('course.noContentDescription')}
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {course.sections.map((section, index) => (
                        <SectionCard
                            key={section.id}
                            section={section}
                            courseSlug={course.slug}
                            isExpanded={expandedSections.has(section.id)}
                            onToggle={() => handleSectionToggle(section.id)}
                            sectionNumber={index + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default CourseContent;
